import React from "react";
import styled from "@emotion/styled";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, Edit, Settings, ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const ToolbarContainer = styled(motion.div)<{ $isDarkMode: boolean }>`
  position: absolute;
  background: ${(props) => (props.$isDarkMode ? "#2c2f36" : "white")};
  border: 1px solid ${(props) => (props.$isDarkMode ? "#4b5563" : "#e5e7eb")};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  padding: 4px;
  gap: 4px;
  z-index: 1000;
  backdrop-filter: blur(8px);
`;

const ToolbarButton = styled(motion.button)<{
  $variant?: "danger" | "primary" | "secondary";
  $isDarkMode: boolean;
}>`
  padding: 8px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  ${(props) => {
    const isDark = props.$isDarkMode;
    switch (props.$variant) {
      case "danger":
        return `
          background: #ef4444;
          color: white;
          &:hover { background: #dc2626; }
        `;
      case "primary":
        return `
          background: #3b82f6;
          color: white;
          &:hover { background: #2563eb; }
        `;
      default:
        return `
          background: ${isDark ? "#374151" : "#f3f4f6"};
          color: ${isDark ? "#e5e7eb" : "#374151"};
          &:hover {
            background: ${isDark ? "#4b5563" : "#e5e7eb"};
          }
        `;
    }
  }}
`;

interface FloatingToolbarProps {
  position: { x: number; y: number };
  visible: boolean;
  onDelete: () => void;
  onEdit?: () => void;
  onSettings?: () => void;
  type: "node" | "edge";
  onNudgeSourceHandle?: (dir: "left" | "right" | "top" | "bottom") => void;
  onNudgeTargetHandle?: (dir: "left" | "right" | "top" | "bottom") => void;
  sourceName?: string;
  targetName?: string;
}

const GroupLabel = styled.div<{ $isDarkMode: boolean }>`
  font-size: 11px;
  font-weight: 600;
  color: ${(p) => (p.$isDarkMode ? "#e5e7eb" : "#374151")};
  margin: 4px 4px 2px;
`;

const Compass = styled.div`
  display: grid;
  grid-template-columns: 28px 28px 28px;
  grid-template-rows: 24px 24px 24px;
  gap: 4px;
  align-items: center;
  justify-items: center;
`;

export const FloatingToolbar: React.FC<FloatingToolbarProps> = ({ position, visible, onDelete, onEdit, onSettings, type, onNudgeSourceHandle, onNudgeTargetHandle, sourceName, targetName }) => {
  const { isDarkMode } = useTheme();

  return (
    <AnimatePresence>
      {visible && (
        <ToolbarContainer
          $isDarkMode={isDarkMode}
          style={{
            left: position.x,
            top: position.y,
          }}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          {type === "node" && onEdit && (
            <ToolbarButton $variant="primary" $isDarkMode={isDarkMode} onClick={onEdit} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} title="Edit Service">
              <Edit size={16} />
            </ToolbarButton>
          )}

          {type === "node" && onSettings && (
            <ToolbarButton $variant="secondary" $isDarkMode={isDarkMode} onClick={onSettings} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} title="Service Settings">
              <Settings size={16} />
            </ToolbarButton>
          )}

          {type === "edge" && onNudgeSourceHandle && (
            <div style={{ margin: "0 6px" }}>
              <GroupLabel $isDarkMode={isDarkMode}>Source{sourceName ? `: ${sourceName}` : ""}</GroupLabel>
              <Compass>
                <div />
                <ToolbarButton $variant="secondary" $isDarkMode={isDarkMode} onClick={() => onNudgeSourceHandle("top")} title="Source: Top" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <ArrowUp size={14} />
                </ToolbarButton>
                <div />
                <ToolbarButton $variant="secondary" $isDarkMode={isDarkMode} onClick={() => onNudgeSourceHandle("left")} title="Source: Left" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <ArrowLeft size={14} />
                </ToolbarButton>
                <div />
                <ToolbarButton $variant="secondary" $isDarkMode={isDarkMode} onClick={() => onNudgeSourceHandle("right")} title="Source: Right" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <ArrowRight size={14} />
                </ToolbarButton>
                <div />
                <ToolbarButton $variant="secondary" $isDarkMode={isDarkMode} onClick={() => onNudgeSourceHandle("bottom")} title="Source: Bottom" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <ArrowDown size={14} />
                </ToolbarButton>
                <div />
              </Compass>
            </div>
          )}

          {type === "edge" && onNudgeTargetHandle && (
            <div style={{ margin: "0 6px" }}>
              <GroupLabel $isDarkMode={isDarkMode}>Target{targetName ? `: ${targetName}` : ""}</GroupLabel>
              <Compass>
                <div />
                <ToolbarButton $variant="secondary" $isDarkMode={isDarkMode} onClick={() => onNudgeTargetHandle("top")} title="Target: Top" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <ArrowUp size={14} />
                </ToolbarButton>
                <div />
                <ToolbarButton $variant="secondary" $isDarkMode={isDarkMode} onClick={() => onNudgeTargetHandle("left")} title="Target: Left" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <ArrowLeft size={14} />
                </ToolbarButton>
                <div />
                <ToolbarButton $variant="secondary" $isDarkMode={isDarkMode} onClick={() => onNudgeTargetHandle("right")} title="Target: Right" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <ArrowRight size={14} />
                </ToolbarButton>
                <div />
                <ToolbarButton $variant="secondary" $isDarkMode={isDarkMode} onClick={() => onNudgeTargetHandle("bottom")} title="Target: Bottom" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <ArrowDown size={14} />
                </ToolbarButton>
                <div />
              </Compass>
            </div>
          )}

          <ToolbarButton $variant="danger" $isDarkMode={isDarkMode} onClick={onDelete} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} title={type === "node" ? "Delete Service" : "Delete Connection"}>
            <Trash2 size={16} />
          </ToolbarButton>
        </ToolbarContainer>
      )}
    </AnimatePresence>
  );
};
